import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { employeeApi, expenseApi, approverApi, expenseApproverApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { PlusCircle, ArrowRight, Calendar, Clock, TrendingUp, Receipt, ClipboardCheck } from 'lucide-react';
import { formatDays } from '@/lib/utils';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'default' };

export default function EmployeeDashboard() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user, isApprover } = useCurrentUser();
  const [balances, setBalances] = useState({});
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pending-approval counts for the approver cards. Fetched in a
  // dedicated effect gated on isApprover so plain employees never hit
  // the role-gated queue endpoints (which would 403).
  const [approvalCounts, setApprovalCounts] = useState({ leave: 0, expense: 0 });

  useEffect(() => {
    async function load() {
      try {
        const [balRes, reqRes, expRes] = await Promise.all([
          employeeApi.getBalances(),
          employeeApi.listRequests(),
          expenseApi.list().catch(() => ({ reports: [] })),
        ]);
        setBalances(balRes.balances || {});
        setLeaveTypes(balRes.leaveTypes || []);
        setRequests((reqRes.requests || []).slice(0, 5));
        setExpenses((expRes.reports || []).slice(0, 5));
        setError(null);
      } catch (err) {
        console.error('Dashboard load failed:', err);
        setError(err.message);
        toast.error(`Failed to load dashboard: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Approver-only: load pending counts once we know the user is an
  // approver. Separate from the main load so non-approvers don't call
  // the withApprover-gated endpoints. isApprover resolves async via
  // useCurrentUser, so this re-runs when it flips true.
  useEffect(() => {
    if (!isApprover) return;
    let cancelled = false;
    (async () => {
      const [lq, eq] = await Promise.all([
        approverApi.getQueue().catch(() => ({ queue: [] })),
        expenseApproverApi.getQueue().catch(() => ({ reports: [] })),
      ]);
      if (cancelled) return;
      setApprovalCounts({
        leave: (lq.queue || []).length,
        expense: (eq.reports || []).length,
      });
    })();
    return () => { cancelled = true; };
  }, [isApprover]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" />
      </div>
    );
  }

  const today = new Date().toISOString().substring(0, 10);
  const upcoming = requests.filter((r) => r.status === 'approved' && r.startDate >= today);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Welcome back, {user?.displayName?.split(' ')[0] || 'there'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Your leave balance and recent activity</p>
        </div>
        <Button onClick={() => navigate('/planner')}>
          <PlusCircle className="h-4 w-4" /> Open Planner
        </Button>
      </div>

      {/* Approver shortcuts — only for users with approver privileges.
          Two cards linking to the leave + expense approval queues, with
          a pending count badge. Hidden entirely for plain employees. */}
      {isApprover && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card
            onClick={() => navigate('/approvals')}
            className="cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Leave Approvals</p>
                <p className="text-xs text-muted-foreground">
                  {approvalCounts.leave > 0
                    ? `${approvalCounts.leave} awaiting your review`
                    : 'Nothing waiting on you'}
                </p>
              </div>
              {approvalCounts.leave > 0 && <Badge variant="warning">{approvalCounts.leave}</Badge>}
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card
            onClick={() => navigate('/expense-approvals')}
            className="cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-profun-teal/10 text-profun-teal">
                <Receipt className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Expense Approvals</p>
                <p className="text-xs text-muted-foreground">
                  {approvalCounts.expense > 0
                    ? `${approvalCounts.expense} awaiting your review`
                    : 'Nothing waiting on you'}
                </p>
              </div>
              {approvalCounts.expense > 0 && <Badge variant="warning">{approvalCounts.expense}</Badge>}
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty state when no leave types */}
      {leaveTypes.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {error
                ? 'Could not load balances. Make sure you are accessing the app through the SWA proxy (port 4280).'
                : 'No leave types configured yet. Ask your admin to set up leave types.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Upcoming (left, compact) + Year-to-date (right, sized to fit text) */}
      <div className="grid gap-6 lg:grid-cols-5">
        {/* Upcoming leave — compact card on the left */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Upcoming Leave</h2>
              {upcoming.length > 0 && (
                <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary tabular-nums">
                  {upcoming.length}
                </span>
              )}
            </div>
            {upcoming.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center">
                <p className="text-xs text-muted-foreground">No upcoming leave.</p>
                <Link to="/planner" className="mt-1 inline-block text-xs font-medium text-primary hover:underline">
                  Plan time off →
                </Link>
              </div>
            ) : (
              <div className="max-h-[280px] space-y-2 overflow-y-auto pr-1">
                {upcoming.map((r) => (
                  <div
                    key={r.rowKey}
                    onClick={() => navigate(`/my-requests/${encodeURIComponent(r.rowKey)}`)}
                    className="cursor-pointer rounded-lg border border-border bg-muted/50 p-2.5 transition-colors hover:border-primary/30 hover:bg-accent"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ background: r.typeColor || 'hsl(var(--muted-foreground))' }}
                      />
                      <span className="truncate text-sm font-medium text-foreground">{r.typeName || r.typeId}</span>
                      <span className="ml-auto shrink-0 rounded-full bg-background px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground tabular-nums">
                        {formatDays(r.days)}d
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {r.startDate} → {r.endDate}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Year-to-date + balance snapshot (merged) — wider so text fits */}
        {leaveTypes.length > 0 && (
          <Card className="lg:col-span-3">
            <CardContent className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-profun-teal" />
                <h2 className="text-sm font-semibold text-foreground">Year-to-Date Usage</h2>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {leaveTypes.map((type) => {
                  const bal = balances[type.rowKey] || { allowance: type.defaultAllowance || 0, used: 0, pending: 0, remaining: type.defaultAllowance || 0 };
                  const total = (bal.allowance || 0) + (bal.carriedOver || 0);
                  const ratio = total > 0 ? bal.remaining / total : 1;
                  const pct = total > 0 ? Math.round((bal.remaining / total) * 100) : 100;
                  const barColor = ratio > 0.5 ? '#15D2A4' : ratio > 0.25 ? '#FFCA03' : '#FC026F';

                  return (
                    <div key={type.rowKey} className="rounded-lg bg-muted/50 p-3" title={type.displayName}>
                      {/* Type label — allow 2-line wrap so long names stay visible */}
                      <div className="flex items-start gap-1.5">
                        <span
                          className="mt-1 h-2 w-2 rounded-full shrink-0"
                          style={{ background: type.color || 'hsl(var(--muted-foreground))' }}
                        />
                        <span className="text-[11px] font-medium leading-tight text-muted-foreground break-words">
                          {type.displayName}
                        </span>
                      </div>
                      {/* Big used number */}
                      <div className="mt-1 flex items-baseline gap-1">
                        <span className="text-2xl font-bold tabular-nums text-foreground">{formatDays(bal.used || 0)}</span>
                        <span className="text-[10px] text-muted-foreground">used</span>
                      </div>
                      {/* Mini progress bar (remaining) */}
                      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: barColor }}
                        />
                      </div>
                      {/* Remaining / pending row */}
                      <div className="mt-1.5 flex items-center justify-between text-[10px] text-muted-foreground tabular-nums">
                        <span><span className="font-semibold text-foreground">{formatDays(bal.remaining)}</span>/{formatDays(total)} left</span>
                        {(bal.pending || 0) > 0 && <span className="text-profun-amber">{formatDays(bal.pending)} pending</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent requests */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Recent Requests</h2>
          </div>
          <Link to="/my-requests" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-sm text-muted-foreground">No requests yet. Submit your first leave request!</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="divide-y divide-border">
              {requests.map((r) => (
                <div
                  key={r.rowKey}
                  onClick={() => navigate(`/my-requests/${encodeURIComponent(r.rowKey)}`)}
                  className="flex cursor-pointer items-center justify-between px-5 py-3.5 transition-colors hover:bg-accent/50"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: r.typeColor || 'hsl(var(--muted-foreground))' }}
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {r.typeName || r.typeId} &middot; {formatDays(r.days)} day{r.days !== 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {r.startDate} to {r.endDate}
                      </div>
                    </div>
                  </div>
                  <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      {/* Recent expense reports */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Recent Expenses</h2>
          </div>
          <Link to="/expenses" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {expenses.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No expense reports yet.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="divide-y divide-border">
              {expenses.map((e) => (
                <div
                  key={e.rowKey}
                  onClick={() => navigate(`/expenses/${encodeURIComponent(e.rowKey)}`)}
                  className="flex cursor-pointer items-center justify-between px-5 py-3.5 transition-colors hover:bg-accent/50"
                >
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {e.title || 'Expense Report'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {e.totalAmount != null ? `$${Number(e.totalAmount).toFixed(2)}` : ''} {e.submittedAt ? `· ${new Date(e.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                    </div>
                  </div>
                  <Badge variant={STATUS_VARIANT[e.status] || 'default'}>{e.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
